/**
 * The portal's top bar — the things that belong ABOVE every screen rather
 * than inside one: the command palette trigger, the language switch and the
 * approvals bell.
 *
 * UI_CONSOLIDATION_PLAN 4B + 4C. Until now the portal had no top bar at all
 * (`Base.js` was sidebar + content), which is why Approvals had to live as a
 * nav item and why there was nowhere to say "Ctrl-K opens everything". A
 * shortcut nobody is told about is a shortcut nobody uses, so the trigger is
 * visible and shows its own key.
 *
 * Deliberately thin and transparent: it must not steal vertical space from the
 * page headers every hub already draws, and it must not look like a second
 * navigation. The bell hides itself without the `approvals` capability, and on
 * a narrow screen the palette trigger collapses to its icon.
 */
import React from 'react';
import { Search } from 'lucide-react';

import ApprovalsBell from './ApprovalsBell';
import CommandPalette from './CommandPalette';
import { useT, useLang, LANGUAGES } from '../../i18n';

const isMac = () => {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform || '');
};

// The palette owns its own open state and listens for this event, so the
// trigger needs no shared context and there is only ever one palette mounted.
const openPalette = () =>
  window.dispatchEvent(new CustomEvent('inikru:open-command-palette'));

const FOCUS = 'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B40A0A]';

/**
 * EN / हि. The language layer is a module singleton (src/i18n), so this is two
 * buttons and one call — no provider. The sidebar, which remounts on every
 * route change, reads the new words on its next render; everything already
 * mounted re-renders through `useT()`. The same switch lives in Store
 * Settings → General for people who look for it there.
 */
const LangToggle = () => {
  const t = useT();
  const [lang, setLang] = useLang();
  return (
    <div
      role="radiogroup"
      aria-label={t('lang.switch_aria', 'Change the portal language')}
      className="inline-flex items-center rounded-full border border-line bg-white p-0.5"
    >
      {LANGUAGES.map((l) => {
        const active = l.code === lang;
        return (
          <button
            key={l.code}
            type="button"
            role="radio"
            aria-checked={active}
            lang={l.code}
            title={l.label}
            onClick={() => setLang(l.code)}
            className={`h-7 min-w-[34px] px-2 rounded-full text-[11.5px] font-display font-bold leading-none transition-colors ${FOCUS} ${
              active ? 'bg-ink text-white' : 'text-muted hover:text-ink'
            }`}
          >
            {l.short}
          </button>
        );
      })}
    </div>
  );
};

const TopBar = () => {
  const t = useT();
  return (
    <>
      <div className="flex items-center justify-end gap-2 px-4 pt-3">
        <button
          type="button"
          onClick={openPalette}
          className={`inline-flex items-center gap-2 rounded-full border border-line bg-white px-3 py-2 text-[12.5px] text-muted transition-colors hover:bg-gray-50 ${FOCUS}`}
          aria-label={t('topbar.search_aria', 'Search and jump to any screen')}
        >
          <Search className="h-4 w-4" />
          <span className="hidden sm:inline">{t('topbar.search_placeholder', 'Search or jump to…')}</span>
          <kbd className="hidden sm:inline rounded border border-line px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
            {isMac() ? '⌘K' : 'Ctrl K'}
          </kbd>
        </button>
        <LangToggle />
        <ApprovalsBell />
      </div>
      <CommandPalette />
    </>
  );
};

export default TopBar;
